import { queryOptions } from '@tanstack/vue-query'
import merge from 'lodash/merge'
import i18n from '@/plugins/i18n'
import { queryClient } from '@/plugins/tanstack'
import { useSessionStore } from '@/common/stores/sessionStore'
import { getLocalizedMessagesV2 } from '@/common/services/LocalizationService'
import { invalidateQuery, queryKeyFactory } from '@/common/services/serverData/serverDataManager'
import { type GroupOptionsBase, SERVER_DATA_SCOPE } from '@/common/types/cacheConstants'
import { type i18nMessagesDTOV2, type i18nTranslation, type PrefixTranslationsEnum } from '@/common/types/i18n'

const transformTranslationToI18nFormat = (list: i18nMessagesDTOV2[], prefix: string): i18nTranslation => {
  return list.reduce((result, { key, message }: i18nMessagesDTOV2) => {
    // check if assist response is being processed AND key starts with assist.$vuetify
    // THEN remove assist prefix
    const isAssistVuetifyPrefix = prefix === 'assist' && key.startsWith('assist.$vuetify')
    const i18nKey = isAssistVuetifyPrefix ? key.slice(7) : key

    result[i18nKey] = message
    return result
  }, {} as i18nTranslation)
}

export const getTranslationsInI18nFormat = async (locale: string, prefixes: Array<string> | string) => {
  const _prefixes = typeof prefixes == 'string' ? [prefixes] : prefixes

  const translationPromises = _prefixes.map((prefix) =>
    getLocalizedMessagesV2({
      'is-exact-key': false,
      key: prefix,
      locale,
    }),
  )

  const translations = await Promise.all(translationPromises)

  const i18nTranslations: i18nTranslation = translations.reduce((result, translationByPrefix, index) => {
    const currentPrefix = _prefixes[index]
    const prefixedTranslations = transformTranslationToI18nFormat(translationByPrefix, currentPrefix)

    return { ...result, ...prefixedTranslations }
  }, {} as i18nTranslation)
  return i18nTranslations
}

interface GroupOptions extends GroupOptionsBase {
  prefix: PrefixTranslationsEnum
  locale: string
}
const groupOptions = (options: GroupOptions) => {
  const { locale, prefix, ...config } = options
  const defaultConfig = {
    queryKey: queryKeyFactory({
      scope: SERVER_DATA_SCOPE.I18n,
      params: prefix,
    }),
    staleTime: Infinity,
    queryFn: async () => {
      const translations = await getTranslationsInI18nFormat(locale, prefix)
      i18n.global.mergeLocaleMessage(i18n.global.locale.value, translations)
      return prefix
    },
  }
  return queryOptions(merge(defaultConfig, config))
}

type FetchOption = Omit<GroupOptions, 'prefix' | 'locale'> & {
  prefixes: PrefixTranslationsEnum | PrefixTranslationsEnum[]
}
const fetch = (options: FetchOption) => {
  const { prefixes, staleTime } = options
  const sessionStore = useSessionStore()
  const _prefixes = typeof prefixes == 'string' ? [prefixes] : prefixes

  const translationPromises = _prefixes.map((prefix) =>
    queryClient.fetchQuery(
      groupOptions({
        locale: sessionStore.locale,
        prefix,
        staleTime,
      }),
    ),
  )

  return Promise.all(translationPromises)
}

function invalidate(prefix: PrefixTranslationsEnum) {
  invalidateQuery([
    queryKeyFactory({
      scope: SERVER_DATA_SCOPE.I18n,
      params: prefix,
    }),
  ])
}

export default {
  fetch,
  groupOptions,
  invalidate,
}
